import { useState } from "react";
import { Box, Button, Stack, Typography, CircularProgress, IconButton, Pagination, Modal, Fade, Backdrop } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CardShell from "./CardShell";
import { downloadPayrollFile, deleteDraftClosure, regenerateAccountantReport } from "../api/liquidacionApi";
import { fmtCompactDateRange } from "../../../../shared/utils/dateFormat";

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function unwrapHttp(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

function ClosureItem({ closure, onViewDatos, onViewResumen, onResume, onConfirmDelete }) {
  const count = closure.employeesCount || closure.employees?.length || 0;
  const isDraft = closure.status === 'DRAFT';
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleDownloadAccountantReport = async () => {
    setDownloadingReport(true);
    try {
      const updatedClosure = unwrapHttp(await regenerateAccountantReport(closure.id));
      const fileName = updatedClosure?.accountantReportFileId || closure.accountantReportFileId;
      if (!fileName) return;
      const res = await downloadPayrollFile(fileName);
      const blob = new Blob([res.data || res], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar reporte", error);
    } finally {
      setDownloadingReport(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "neutral.50",
        borderRadius: "18px",
        border: "1px solid",
        borderColor: "neutral.300",
        display: "flex",
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: "space-between",
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2,
        transition: "all .2s ease",
        "&:hover": { bgcolor: "neutral.100", borderColor: "primary.300", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" },
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isDraft ? '2fr 1.5fr 1.5fr' : '2fr 1.5fr 1.2fr 1.2fr' }, width: '100%', alignItems: 'center', gap: 2 }}>
        {/* Nombre */}
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.900" }}>
          {closure.name}
        </Typography>

        {/* Fecha Generación */}
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "neutral.600" }}>
            Fecha de generación
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.800" }}>
            {formatDate(closure.createdAt)}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "neutral.600" }}>
            Periodo liquidado
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.800" }}>
            {fmtCompactDateRange(closure.periodStart, closure.periodEnd)}
          </Typography>
        </Box>

        {/* Totales */}
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "neutral.600" }}>
            Cierres totales:
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.800" }}>
            [{count} empleados {isDraft ? 'seleccionados' : 'pagados'}]
          </Typography>
        </Box>

        {!isDraft && (
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "neutral.600" }}>
              Monto total:
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.800" }}>
              {formatCurrency(closure.totalAmount)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Botones de Acción */}
      <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'flex-end', md: 'center' }, flexShrink: 0 }}>
        {isDraft ? (
          <Stack direction="row" spacing={1}>
          <Button
            onClick={() => onResume(closure.id, closure.currentStep)}
            startIcon={<PlayArrowIcon />}
            sx={{
              bgcolor: "primary.900", color: "white", px: 3, py: 0.8, borderRadius: "24px",
              textTransform: "none", fontWeight: 700, fontSize: 13, "&:hover": { bgcolor: "primary.800" },
            }}
          >
            Continuar
          </Button>
          <IconButton 
          onClick={() => onConfirmDelete(closure)} 
          sx={{ 
            bgcolor: 'error.50', 
            color: 'error.700', 
            border: '1px solid',
            borderColor: 'error.200',
            '&:hover': { bgcolor: 'error.100' } 
          }}
        >
          <DeleteIcon />
        </IconButton>
        </Stack>
        ) : (
          <>
            <Button
              onClick={() => onViewDatos(closure.id)}
              startIcon={<VisibilityIcon />}
              sx={{
                bgcolor: "white", color: "primary.900", px: 2, py: 0.8, borderRadius: "24px",
                border: "2px solid", borderColor: "primary.900", textTransform: "none",
                fontWeight: 700, fontSize: 13, "&:hover": { bgcolor: "neutral.100" },
              }}
            >
              Detalle
            </Button>
            <Button
              onClick={() => onViewResumen(closure.id)}
              startIcon={<ReceiptLongIcon />}
              sx={{
                bgcolor: "primary.900", color: "white", px: 2, py: 0.8, borderRadius: "24px",
                border: "2px solid", borderColor: "primary.900", textTransform: "none",
                fontWeight: 700, fontSize: 13, "&:hover": { bgcolor: "primary.800" },
              }}
            >
              Resumen
            </Button>
            {!isDraft && (
              <IconButton
                onClick={handleDownloadAccountantReport}
                disabled={downloadingReport}
                title="Descargar PDF Contador"
                sx={{ bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2', '&:hover': { bgcolor: '#ffcdd2' } }}
              >
                {downloadingReport ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
              </IconButton>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}

export default function PreviousClosuresList({ closures = [], loading, error, onViewDatos, onViewResumen, onResume, page, totalPages, onPageChange }) {
  const [closureToDelete, setClosureToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleExecuteDelete = async () => {
    if (!closureToDelete) return;
    setIsDeleting(true);
    try {
      await deleteDraftClosure(closureToDelete.id);
      window.location.reload();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Error al eliminar el cierre.");
      setIsDeleting(false);
      setClosureToDelete(null);
    }
  };

  if (loading) {
    return (
      <CardShell>
        <Stack spacing={2} sx={{ alignItems: "center", py: 4 }}>
          <CircularProgress size={40} color="primary" />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "primary.900" }}>Cargando cierres...</Typography>
        </Stack>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell>
        <Box sx={{ px: 2, py: 1.2, borderRadius: "18px", bgcolor: "secondary.700", boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "logoblanco" }}>Error al cargar los cierres</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}>{error}</Typography>
        </Box>
      </CardShell>
    );
  }

  if (!closures || closures.length === 0) {
    return (
      <CardShell>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "neutral.700", textAlign: "center", py: 4 }}>
          No hay cierres que coincidan con la búsqueda.
        </Typography>
      </CardShell>
    );
  }

  return (
    <>
      <CardShell>
        <Stack spacing={2}>
          <Stack spacing={1}>
            {closures.map((closure) => (
              <ClosureItem 
                key={closure.id} 
                closure={closure} 
                onViewDatos={onViewDatos} 
                onViewResumen={onViewResumen} 
                onResume={onResume} 
                onConfirmDelete={setClosureToDelete} 
              />
            ))}
          </Stack>

          {/* Paginador */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => onPageChange(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </Stack>
      </CardShell>

      {/*MODAL PERSONALIZADO PARA ELIMINAR CIERRE*/}
      <Modal
        open={Boolean(closureToDelete)}
        onClose={isDeleting ? undefined : () => setClosureToDelete(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 400 } }}
      >
        <Fade in={Boolean(closureToDelete)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 450 }, bgcolor: 'neutral.50', borderRadius: '24px', 
            boxShadow: 24, p: 4, outline: 'none', border: '2px solid', borderColor: 'primary.900'
          }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: '50%', mb: 1 }}>
                <WarningAmberIcon sx={{ fontSize: 48, color: '#f57c00' }} />
              </Box>
              
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'primary.900' }}>
                Atención: Eliminar Cierre
              </Typography>
              
              <Typography sx={{ fontSize: 15, color: 'neutral.700', lineHeight: 1.5 }}>
                Estás a punto de eliminar el cierre en curso <b>{closureToDelete?.name}</b>. Toda la información seleccionada se perderá y no se podrá deshacer.
              </Typography>

              <Stack direction="row" spacing={2} sx={{ width: '100%', mt: 3 }}>
                <Button
                  fullWidth
                  disabled={isDeleting}
                  onClick={() => setClosureToDelete(null)}
                  sx={{ bgcolor: "#ffcdd2", color: "primary.900", py: 1.2, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 15, '&:hover': { bgcolor: "#ffb3ba" } }}
                >
                  Cancelar
                </Button>
                <Button
                  fullWidth
                  disabled={isDeleting}
                  onClick={handleExecuteDelete}
                  sx={{ bgcolor: "#ffe082", color: "primary.900", py: 1.2, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 15, '&:hover': { bgcolor: "#ffd54f" } }}
                >
                  {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Eliminar cierre'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
